var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/components/ErrorBoundary.tsx
import { Typography } from "@mui/material";
import { FlexCol } from "@xylabs/react-flexbox";
import React, { Component } from "react";
var ErrorBoundary = class extends Component {
  static {
    __name(this, "ErrorBoundary");
  }
  constructor(props) {
    super(props);
    this.state = {
      error: void 0
    };
  }
  static getDerivedStateFromError(error) {
    return {
      error
    };
  }
  componentDidCatch(error, errorInfo) {
    console.error(`${error}: ${errorInfo}`);
  }
  render() {
    if (this.state.error) {
      if (this.props.fallbackWithError) {
        return this.props.fallbackWithError(this.state.error);
      }
      return this.props.fallback ?? /* @__PURE__ */ React.createElement(FlexCol, null, /* @__PURE__ */ React.createElement(Typography, {
        variant: "h1"
      }, "Something went wrong."), this.props.scope && /* @__PURE__ */ React.createElement(Typography, {
        variant: "h2"
      }, "[", this.props.scope, "]"), /* @__PURE__ */ React.createElement(Typography, {
        variant: "body1"
      }, "[", this.state.error?.message, "]"));
    }
    return this.props.children;
  }
};

// src/components/ErrorBoundary/ThrownErrorBoundary.tsx
import React6, { Component as Component2 } from "react";

// src/contexts/ErrorReporter/Provider.tsx
import React2 from "react";

// src/contexts/ErrorReporter/Context.ts
import { createContext } from "react";
var ErrorReporterContext = createContext({});

// src/contexts/ErrorReporter/Provider.tsx
var ErrorReporterProvider = /* @__PURE__ */ __name(({ children, rollbar: rollbarProp }) => {
  const rollbar = rollbarProp ?? globalThis.rollbar;
  if (!rollbar) {
    throw new Error("ErrorReporterProvider unable to find a Rollbar instance either passed as prop or from Provider");
  }
  return /* @__PURE__ */ React2.createElement(ErrorReporterContext, {
    value: {
      rollbar
    }
  }, children);
}, "ErrorReporterProvider");

// src/contexts/ErrorReporter/useErrorReporter.tsx
import { use } from "react";
var useErrorReporter = /* @__PURE__ */ __name(() => {
  const context = use(ErrorReporterContext);
  if (context === void 0) {
    console.warn("useErrorReporter must be used within a ErrorReporterContext");
  }
  return context ?? {};
}, "useErrorReporter");

// src/components/ErrorRender/ErrorAlert.tsx
import { ExitToApp as ExitIcon } from "@mui/icons-material";
import { Alert, AlertTitle, Typography as Typography2 } from "@mui/material";
import { ButtonEx } from "@xylabs/react-button";
import React3 from "react";
function ErrorAlert({ action, title = "Whoops! Something went wrong", onCancel, error = "An unknown error occurred", scope, ...props }) {
  return /* @__PURE__ */ React3.createElement(Alert, {
    action,
    severity: "error",
    ...props
  }, /* @__PURE__ */ React3.createElement(AlertTitle, null, title), scope ? /* @__PURE__ */ React3.createElement("div", null, /* @__PURE__ */ React3.createElement(Typography2, {
    variant: "caption",
    mr: 0.5,
    fontWeight: "bold"
  }, "Scope:"), /* @__PURE__ */ React3.createElement(Typography2, {
    variant: "caption"
  }, scope)) : null, /* @__PURE__ */ React3.createElement("div", null, /* @__PURE__ */ React3.createElement(Typography2, {
    variant: "caption",
    mr: 0.5,
    fontWeight: "bold"
  }, "Error:"), /* @__PURE__ */ React3.createElement(Typography2, {
    variant: "caption"
  }, typeof error === "string" ? error : error?.message)), onCancel ? /* @__PURE__ */ React3.createElement(ButtonEx, {
    variant: "outlined",
    size: "small",
    onClick: onCancel,
    position: "absolute",
    style: {
      right: 8,
      top: action ? 42 : 8
    }
  }, /* @__PURE__ */ React3.createElement(ExitIcon, {
    fontSize: "small"
  })) : null);
}
__name(ErrorAlert, "ErrorAlert");

// src/components/ErrorRender/ErrorWithSupport.tsx
import { ButtonEx as ButtonEx2 } from "@xylabs/react-button";
import React4 from "react";
var ErrorRenderWithSupport = /* @__PURE__ */ __name(({ supportHref = "", supportLinkText = "Support", error, onCancel, scope, ...props }) => {
  return /* @__PURE__ */ React4.createElement(ErrorAlert, {
    action: /* @__PURE__ */ React4.createElement(ButtonEx2, {
      target: "_blank",
      color: "inherit",
      size: "small",
      href: supportHref,
      position: "absolute",
      style: {
        right: 8,
        top: 8
      }
    }, supportLinkText),
    error,
    scope,
    onCancel,
    ...props
  });
}, "ErrorRenderWithSupport");

// src/components/ErrorRender/Render.tsx
import { FlexCol as FlexCol2 } from "@xylabs/react-flexbox";
import React5, { useEffect } from "react";
function ErrorRender({ onCancel, error, noErrorDisplay = false, customError = null, children, scope, useLocation, ...props }) {
  const location = useLocation?.();
  useEffect(() => {
    if (location) {
      location.state = {
        from: {
          pathname: globalThis.location.pathname
        }
      };
    }
  }, [
    location
  ]);
  useEffect(() => {
    if (error) {
      globalThis.rollbar?.error(error);
    }
  }, [
    error
  ]);
  return error ? /* @__PURE__ */ React5.createElement(FlexCol2, {
    alignItems: "stretch",
    ...props
  }, noErrorDisplay ? customError : /* @__PURE__ */ React5.createElement(FlexCol2, {
    alignItems: "center",
    ...props
  }, /* @__PURE__ */ React5.createElement(ErrorAlert, {
    error,
    onCancel,
    scope
  }))) : /* @__PURE__ */ React5.createElement(React5.Fragment, null, children);
}
__name(ErrorRender, "ErrorRender");

// src/components/ErrorBoundary/ThrownErrorBoundary.tsx
var ThrownErrorBoundaryInner = class ThrownErrorBoundaryInner2 extends Component2 {
  static {
    __name(this, "ThrownErrorBoundaryInner");
  }
  state = {
    errorEx: void 0
  };
  static getDerivedStateFromError(error) {
    const thrownErrorBoundaryState = {
      hasError: true,
      errorEx: error,
      xyoError: ThrownErrorBoundaryInner2.normalizeError(error)
    };
    return thrownErrorBoundaryState;
  }
  static normalizeError(error) {
    return error;
  }
  componentDidCatch(error, errorInfo) {
    const { rethrow, rollbar } = this.props;
    const { errorEx } = this.state;
    rollbar?.error(error);
    console.error("Error:", errorEx, errorInfo);
    if (rethrow) {
      throw error;
    }
  }
  render() {
    const { errorEx } = this.state;
    const { children, boundaryName, errorComponent, scope, title } = this.props;
    if (errorEx) {
      if (errorComponent) {
        return errorComponent(errorEx, boundaryName);
      }
      return /* @__PURE__ */ React6.createElement(ErrorRender, {
        error: errorEx,
        errorContext: `${boundaryName} Boundary`,
        scope,
        title
      });
    }
    return children;
  }
};
function ThrownErrorBoundary({ rollbar, ...props }) {
  const { rollbar: rollbarErrorReporter } = useErrorReporter();
  return /* @__PURE__ */ React6.createElement(ThrownErrorBoundaryInner, {
    rollbar: rollbar ?? rollbarErrorReporter ?? globalThis.rollbar,
    ...props
  });
}
__name(ThrownErrorBoundary, "ThrownErrorBoundary");

// src/components/Errors/ErrorsViewer.tsx
import { Replay as ReplayIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { FlexGrowCol } from "@xylabs/react-flexbox";
import React8 from "react";

// src/components/Errors/ErrorViewer.tsx
import { Typography as Typography3, useTheme } from "@mui/material";
import { FlexCol as FlexCol3 } from "@xylabs/react-flexbox";
import React7 from "react";
var ErrorViewer = /* @__PURE__ */ __name((props) => {
  const { error, ...boxProps } = props;
  const theme = useTheme();
  return /* @__PURE__ */ React7.createElement(FlexCol3, {
    color: theme.palette.error.main,
    ...boxProps
  }, /* @__PURE__ */ React7.createElement(Typography3, {
    align: "center",
    variant: "subtitle1"
  }, error?.name), /* @__PURE__ */ React7.createElement(Typography3, {
    align: "center",
    variant: "body2"
  }, error?.message));
}, "ErrorViewer");

// src/components/Errors/ErrorsViewer.tsx
var ErrorsViewer = /* @__PURE__ */ __name((props) => {
  const { onRetry, errors, ...boxProps } = props;
  return /* @__PURE__ */ React8.createElement(FlexGrowCol, {
    padding: 1,
    ...boxProps
  }, errors?.map((error, index) => {
    return /* @__PURE__ */ React8.createElement(ErrorViewer, {
      error,
      key: index
    });
  }), onRetry ? /* @__PURE__ */ React8.createElement(IconButton, {
    onClick: onRetry
  }, /* @__PURE__ */ React8.createElement(ReplayIcon, null)) : null);
}, "ErrorsViewer");
export {
  ErrorAlert,
  ErrorBoundary,
  ErrorRender,
  ErrorRenderWithSupport,
  ErrorReporterProvider,
  ErrorViewer,
  ErrorsViewer,
  ThrownErrorBoundary,
  useErrorReporter
};
//# sourceMappingURL=index.mjs.map